/**
 * Help & Guides — practical reference content for /help
 * Calm, scannable guidance (not a full knowledge base).
 */

export const HELP_PAGE = {
  title: 'Help & Guides',
  subtitle:
    'Simple guidance for recording memories, building profiles, preparing memorials and creating legacy outputs.',
  intro:
    'Use these short guides when you are unsure what to do next. You can return anytime — there is no rush to finish everything at once.',
};

/** Short summaries for accordion headers (hub-first scanning). */
export const HELP_QUICK_CARDS = [
  {
    title: 'New here?',
    text: 'Start with one person, then add stories, voice and photos over time.',
    sectionId: 'getting-started',
    linkLabel: 'Open Getting started',
  },
  {
    title: 'Recording not working?',
    text: 'Try a short test recording, check mic permission, then refresh or reconnect your microphone.',
    sectionId: 'voice-recording',
    linkLabel: 'Open Recording tips',
  },
  {
    title: 'Preparing a Legacy Book?',
    text: 'A complete profile with photos, stories and voice memories creates a stronger book preview.',
    sectionId: 'legacy-book',
    linkLabel: 'Open Legacy Book guide',
  },
];

export const HELP_DEFAULT_OPEN_SECTIONS = ['getting-started', 'voice-recording'];

export const HELP_NAV_ITEMS = [
  { id: 'getting-started', label: 'Getting started' },
  { id: 'voice-recording', label: 'Voice memories' },
  { id: 'story-studio', label: 'AI Story Studio' },
  { id: 'person-profiles', label: 'Person profiles' },
  { id: 'memorial-bridge', label: 'Memorial Bridge' },
  { id: 'keepsakes', label: 'Memorial Photo Link' },
  { id: 'handover-vault', label: 'Handover Vault' },
  { id: 'legacy-book', label: 'Legacy Book & PDF' },
  { id: 'family-gathering-room', label: 'Family Gathering Room' },
  { id: 'account-privacy', label: 'Account & privacy' },
  { id: 'troubleshooting', label: 'Quick fixes' },
];

export const HELP_GUIDE_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting started',
    summary: 'Begin with one person and add what you know at your own pace.',
    what: 'MyLegacyBridge is a private family archive. You begin with one person and add what you know — photos, stories, voice, documents and connections — at your own pace.',
    howTo: [
      'Create or open your family archive from the dashboard.',
      'Add your first person — often yourself or someone you wish to honour.',
      'Add a profile photo and a short life summary when you can.',
      'Record or write at least one story, or save a voice memory.',
      'Return later to invite family, add documents, or prepare memorial and Legacy Book outputs.',
    ],
    troubleshooting: null,
    why: 'Start with one person. Add what you know. You can always return later.',
    callout: 'The person profile is the foundation for Legacy Books, memorial pages and keepsakes.',
  },
  {
    id: 'voice-recording',
    title: 'Recording voice memories',
    summary: 'Save tone and stories in voice — test your mic before recording something important.',
    what: 'Voice memories preserve tone, laughter and the way someone told a story. A short test recording helps avoid losing something important because of browser or microphone issues.',
    howTo: [
      'Make a quick 5-second test recording first.',
      'Play it back before recording something important.',
      'Delete the test if it worked.',
      'Speak clearly and stay close enough to the microphone.',
      'Record in a quiet place if possible.',
    ],
    troubleshooting: [
      'Refresh the page and try again.',
      'Allow microphone permission in your browser.',
      'Check the browser is using the correct microphone (system settings or browser device picker).',
      'Unplug and reconnect a USB microphone or headset.',
      'Close other apps that may be using the microphone.',
      'Try a short test recording again.',
      'On mobile, confirm the browser has microphone permission in device settings.',
    ],
    why: 'A short test helps prevent losing an important memory because of microphone or browser issues.',
    callout:
      'Voice limits may vary by plan. Shorter voice notes may be available on free tiers; longer recordings and more transcription may be included on paid archives. Check Pricing for current limits.',
  },
  {
    id: 'story-studio',
    title: 'AI Story Studio',
    summary: 'Gentle prompts when you are not sure what to write — type, record or save a draft.',
    what: 'Story Studio and Storybook give gentle prompts when you are not sure what to write. You can type, record voice, or save a draft and return later.',
    howTo: [
      'Open Story Studio or Storybook from the dashboard or a person profile.',
      'Choose a prompt that feels right — one memory at a time.',
      'Write, record voice, or leave a draft for later.',
      'Stories attach to the person they belong to and can support profiles, Legacy Books and memorial pages.',
      'You do not need to complete everything in one sitting.',
    ],
    troubleshooting: [
      'Save drafts regularly when the editor supports it.',
      'Refresh if the editor feels stuck.',
      'Keep each story focused on one memory.',
      'Use voice recording if writing feels difficult.',
    ],
    why: 'Small memories build into a fuller life story over time.',
  },
  {
    id: 'person-profiles',
    title: 'Person profiles',
    summary: 'Photo, summary, stories, voice and documents in one calm place.',
    what: 'A person profile brings together photo, life summary, themes, dates, stories, voice, documents and family connections in one calm place.',
    howTo: [
      'Start with name, photo and life summary.',
      'Add known-for themes and what they loved when you can.',
      'Add at least three stories if you are preparing for a Legacy Book.',
      'Save voice memories and link photos or documents.',
      'Connect parents, partner, children or siblings when relationships are known.',
      'Use profile guidance or the checklist on the profile if it is shown.',
    ],
    troubleshooting: null,
    why: 'A complete profile makes the Legacy Book, memorial page and family archive feel more meaningful.',
  },
  {
    id: 'memorial-bridge',
    title: 'Memorial Bridge',
    summary: 'Prepare a respectful tribute page — private until your family is ready to share.',
    what: 'Memorial Bridge lets families prepare a respectful tribute page — private until you choose to share. Stories, photos, voice and moderated tributes can help people remember someone properly.',
    howTo: [
      'Open Memorial Bridge from a person profile when the time feels right.',
      'Keep the memorial private while you gather content.',
      'Add cover image, life story and gallery photos when ready.',
      'Review privacy and tribute settings before publishing.',
      'Share the memorial link only when your family is ready.',
    ],
    troubleshooting: [
      'Check privacy settings before sharing.',
      'Preview the memorial before publishing.',
      'Make sure the person profile has enough stories and photos.',
      'Review text and images before giving wider family access.',
    ],
    why: 'A memorial page gives family members one calm place to remember, rather than scattered posts and messages.',
  },
  {
    id: 'keepsakes',
    title: 'Memorial Photo Link & keepsakes',
    summary: 'Design a keepsake preview and save a request for founder review when available.',
    what: 'You can prepare a Memorial Photo Link or keepsake request from the memorial workspace. Paid archive members may upload a persisted image and save a request for founder review. Payment, proof approval, shipping and QR/NFC fulfilment may still be staged depending on the current release.',
    howTo: [
      'Open the memorial workspace for the person.',
      'Design a Memorial Photo Link preview when available.',
      'Paid members: upload a clear JPG, PNG or WebP image and save the request.',
      'Wait for founder review — fulfilment is not automatic yet.',
    ],
    troubleshooting: [
      'Use JPG, PNG or WebP.',
      'Avoid very large files — try a smaller or clearer image if upload fails.',
      'If preview does not appear, refresh and try again.',
      'Do not close the page while an upload is processing.',
    ],
    why: 'Saving a request helps prepare the keepsake before payment and fulfilment are enabled.',
    callout:
      'Payment checkout, proof approval, shipping and physical QR/NFC fulfilment are not fully live yet. Requests are reviewed manually.',
  },
  {
    id: 'handover-vault',
    title: 'Family Handover Vault',
    summary: 'Practical guidance for family — contacts, documents and wishes in plain language.',
    what: 'The Handover Vault holds practical guidance for family — trusted contacts, where important documents are kept, wishes and instructions someone may need later.',
    howTo: [
      'Add entries in plain language — one topic at a time.',
      'Include who to contact and where key documents live.',
      'Update entries when circumstances change.',
      'Review privacy — only store what you are comfortable keeping in your archive.',
    ],
    troubleshooting: [
      'Keep entries clear and simple.',
      'Update when phone numbers, locations or wishes change.',
      'Avoid uploading anything you are not comfortable storing digitally.',
      'Check who can access your archive before adding sensitive details.',
    ],
    why: 'It helps family members avoid confusion at difficult times.',
    callout: 'This is not legal advice and does not replace a will or professional guidance.',
  },
  {
    id: 'legacy-book',
    title: 'Legacy Book & PDF export',
    summary: 'Preview a keepsake book from a complete profile, or export an archive PDF snapshot.',
    what: 'A basic archive PDF export is available from profile and archive settings. The web-based Legacy Book preview shows how a person could appear in a keepsake book — polished print layout and QR links will improve over time.',
    howTo: [
      'Add a profile photo and life summary.',
      'Add three or more stories when you can.',
      'Link photos, documents and voice memories.',
      'Open Preview Legacy Book from the person profile.',
      'Use Export person PDF separately when you need a downloadable archive snapshot.',
    ],
    troubleshooting: null,
    why: 'The Legacy Book turns an online archive into a tangible family keepsake.',
    callout:
      'Final polished export styling is still improving. The preview shows direction — not the finished print product yet.',
  },
  {
    id: 'family-gathering-room',
    title: 'Family Gathering Room',
    summary:
      'A private online space for invited family to share memories, identify photos, or plan what to preserve together.',
    what: 'A Family Gathering Room gives invited family members a private online space to talk, share memories, identify old photos, or plan what to preserve together.',
    howTo: [
      'Decide what the meeting is for.',
      'Invite only the family members you want involved.',
      'Use the room to share stories, ask questions, or plan the Legacy Book.',
      'Add any useful memories back into the archive afterwards.',
    ],
    meetingIdeas: [
      'Share memories about a loved one',
      'Identify people or places in old photos',
      'Plan a Legacy Book',
      'Prepare a memorial page',
      'Ask older relatives gentle story prompts',
      'Reconnect before funerals or weddings become the only time everyone meets',
    ],
    troubleshooting: [
      'Request a room from the dashboard or account settings — rooms are arranged manually while this feature is being introduced.',
      'Allow time for support to confirm availability and send a private link.',
      'Test your microphone and camera before the meeting if you plan to use them.',
      'Use a quiet space and invite only the people you trust.',
      'Contact support@mylegacybridge.com if you need to reschedule or have access issues.',
    ],
    why: 'Families often wait until funerals or weddings to come together. A Family Gathering Room gives them a calmer way to reconnect and preserve memories while they can.',
    callout:
      'MyLegacyBridge does not record Family Gathering Rooms. If a family chooses to record using their own device or software, they should make sure everyone agrees first. Rooms are subject to availability and may be prioritised for Legacy members.',
  },
  {
    id: 'account-privacy',
    title: 'Account, login & privacy',
    summary: 'Sign-in habits, invites and privacy settings that keep your archive under your control.',
    what: 'Use the same email consistently. Google sign-in and email/password may behave differently depending on how your account was created. Privacy settings and invites control who sees your archive.',
    howTo: [
      'Sign in with the email you used to register.',
      'Use password reset from the login page if needed.',
      'Review archive and memorial privacy before inviting family.',
      'Only invite people you trust.',
    ],
    troubleshooting: [
      'Refresh if login appears stuck.',
      'Check spam or junk folders for invitation or reset emails.',
      'Use Forgot password on the login page.',
      'Try another browser if sign-in issues persist.',
    ],
    why: 'Clear account habits and careful sharing keep your family archive private until you choose otherwise.',
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting quick fixes',
    summary: 'Calm first steps before contacting support.',
    what: 'Most issues are simple to resolve. Try these calm steps before contacting support.',
    howTo: null,
    troubleshooting: [
      'Refresh the page.',
      'Check your internet connection.',
      'Check browser permissions (microphone, camera if used).',
      'Try a different browser.',
      'Clear cache if a page looks outdated after an update.',
      'Log out and sign back in.',
      'Contact support@mylegacybridge.com if the issue continues.',
    ],
    why: null,
  },
];

/** Anchor path for contextual links */
export function helpSectionHref(sectionId) {
  return sectionId ? `/help#${sectionId}` : '/help';
}
